// lib/safe-area-probe.ts
//
// iOS standalone PWAs (viewport-fit=cover + apple-mobile-web-app-status-bar-style
// black-translucent) can report wrong/stale env(safe-area-inset-*) values on
// cold start — confirmed on a real device (iPhone 17 Pro Max, iOS 26):
// window.innerHeight measured 894px against a real screen.height of 956px,
// a 62px gap with no corresponding visible UI, reproducible only in the
// installed PWA and never in Safari. Documented WebKit bug; see
// https://gist.github.com/fozzedout/5e77925381991a9570151550992baf14 ("iOS
// PWA Game UI" guide) for the technique this module implements:
//   1. Measure env() through a real DOM element's offsetHeight (forces a
//      synchronous layout, returning WebKit's actual resolved value rather
//      than a potentially-stale CSS variable read).
//   2. Re-measure at increasing delays after load, since the correct value
//      sometimes only becomes available slightly after first paint.
//   3. Briefly toggle the viewport meta's `viewport-fit` cover -> auto ->
//      cover, which forces WebKit to recompute env() without requiring the
//      user to physically rotate the device (rotation is the other known
//      trigger, per the same guide, but isn't something we can do for them).
//
// Measured values are written to CSS custom properties on :root
// (--safe-area-inset-top/right/bottom/left), which globals.css/components
// read instead of raw env() — see globals.css's #app-shell padding-bottom.

const SIDES = ["top", "right", "bottom", "left"] as const;
type Side = (typeof SIDES)[number];

function measureEnvInset(side: Side): number {
  const el = document.createElement("div");
  el.style.cssText = `position:fixed;top:0;left:0;width:0;height:env(safe-area-inset-${side},0px);visibility:hidden;pointer-events:none`;
  document.body.appendChild(el);
  const value = el.offsetHeight;
  el.remove();
  return value;
}

function applyMeasuredInsets(): void {
  const root = document.documentElement.style;
  for (const side of SIDES) {
    root.setProperty(`--safe-area-inset-${side}`, `${measureEnvInset(side)}px`);
  }
}

function toggleViewportFitAndRemeasure(): void {
  // Only standalone PWAs hit the stale-on-cold-start bug this guide
  // documents; a normal Safari tab doesn't need (or benefit from) the
  // toggle, and there's no reason to risk a visible reflow there.
  if (!("standalone" in window.navigator) || !window.navigator.standalone) return;

  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;
  const original = meta.getAttribute("content");
  if (!original || !original.includes("viewport-fit=cover")) return;

  meta.setAttribute("content", original.replace("viewport-fit=cover", "viewport-fit=auto"));
  requestAnimationFrame(() => {
    meta.setAttribute("content", original);
    requestAnimationFrame(applyMeasuredInsets);
  });
}

export function initSafeAreaProbe(): void {
  applyMeasuredInsets();
  // Re-measure on the documented delays — the correct value sometimes only
  // becomes available slightly after first paint on a PWA cold start.
  setTimeout(applyMeasuredInsets, 100);
  setTimeout(applyMeasuredInsets, 500);
  setTimeout(applyMeasuredInsets, 1000);
  // Force a real recalculation rather than trusting whatever the above
  // measured — cheap, and the gist's "ONLY confirmed cold-start fix besides
  // physically rotating the device" for this specific bug.
  toggleViewportFitAndRemeasure();

  window.addEventListener("resize", applyMeasuredInsets);
  window.addEventListener("orientationchange", () => setTimeout(applyMeasuredInsets, 300));
}
